/**
 * analysisRange.js : plage d'analyse de la comparaison de sessions (pur, testable).
 *
 * Pourquoi : deux sessions de durées différentes ne se comparent qu'à période
 * égale. La plage choisie pilote courbes, statistiques, coches et PDF.
 * Conçu pour resservir sur la page d'une session seule (aucune dépendance à la
 * comparaison hors des relevés `{ session, readings }`).
 *
 * Toutes les bornes sont en millisecondes epoch (jamais en heure locale) :
 *   - « Heure réelle » (align clock) : une même fenêtre [start, end] pour toutes
 *     les sessions ;
 *   - « Depuis le début » (align fromStart) : une durée depuis le début de chaque
 *     session (session.start_time, origine de l'axe de compareAxis.js), donc une
 *     fenêtre propre à chaque session.
 *
 * Sélection (ce que l'utilisateur a choisi, sérialisée dans l'URL) :
 *   { align: 'clock'|'fromStart', range: 'common'|'all'|'custom', from?, to? }
 * `custom` n'existe qu'en heure réelle (from/to en ms epoch).
 */
import { ALIGN_MODES } from '../charts/compareAxis';
import { isFiniteNumber } from '../utils/statsUtils';

export const RANGES = Object.freeze({ COMMON: 'common', ALL: 'all', CUSTOM: 'custom' });

// Couverture minimale d'une session dans la plage avant avertissement : en
// dessous de 80 %, la session n'a pas mesuré une part notable de la période
// comparée (début tardif, fin précoce, trous sans ligne).
export const MIN_COVERAGE = 0.8;
// Mesures minimales d'un champ dans la plage pour une coche « meilleure
// valeur » : en dessous de 30 valeurs (30 min à la minute, 1 h toutes les
// 2 min), une moyenne ou un P95 ne se compare pas sérieusement.
export const MIN_MEASURES_FOR_BEST = 30;
// Plafond d'un intervalle entre deux mesures consécutives dans le calcul de la
// couverture : 3 fois l'intervalle médian de la session (suit la cadence du
// capteur), au moins 2 minutes. Au-delà, l'intervalle est un trou.
const GAP_FACTOR = 3;
const MIN_GAP_CAP_MS = 2 * 60000;

const toMs = (date) => new Date(date).getTime();
const ALIGNS = Object.values(ALIGN_MODES);

/** [première, dernière] mesure d'une session en ms, ou null sans mesure. */
export function sessionSpan(s) {
  const readings = s?.readings || [];
  if (!readings.length) return null;
  return { start: toMs(readings[0].timestamp), end: toMs(readings[readings.length - 1].timestamp) };
}

// Durée depuis le début de la session (start_time) jusqu'à sa dernière mesure.
function elapsedFromStart(s) {
  const span = sessionSpan(s);
  if (!span) return null;
  const origin = toMs(s.session?.start_time);
  return Number.isFinite(origin) ? Math.max(0, span.end - origin) : span.end - span.start;
}

const sessionOrigin = (s) => {
  const origin = toMs(s.session?.start_time);
  return Number.isFinite(origin) ? origin : sessionSpan(s)?.start;
};

/** Plage complète en heure réelle : { start, end }, ou null sans mesure. */
export function clockFullRange(loaded) {
  const spans = loaded.map(sessionSpan).filter(Boolean);
  if (!spans.length) return null;
  return { start: Math.min(...spans.map(r => r.start)), end: Math.max(...spans.map(r => r.end)) };
}

/** Plage commune en heure réelle (intersection), ou null sans chevauchement. */
export function clockCommonRange(loaded) {
  const spans = loaded.map(sessionSpan).filter(Boolean);
  if (spans.length < 2) return null;
  const start = Math.max(...spans.map(r => r.start));
  const end = Math.min(...spans.map(r => r.end));
  return start < end ? { start, end } : null;
}

/** Durée commune « depuis le début » (plus courte session), ou null. */
export function commonElapsed(loaded) {
  const durations = loaded.map(elapsedFromStart).filter(isFiniteNumber);
  return durations.length ? Math.min(...durations) : null;
}

/** Durée totale « depuis le début » (plus longue session), ou null. */
export function fullElapsed(loaded) {
  const durations = loaded.map(elapsedFromStart).filter(isFiniteNumber);
  return durations.length ? Math.max(...durations) : null;
}

/** Sélection par défaut : heure réelle et plage commune si chevauchement, sinon durée commune. */
export function defaultSelection(loaded) {
  return clockCommonRange(loaded)
    ? { align: ALIGN_MODES.CLOCK, range: RANGES.COMMON }
    : { align: ALIGN_MODES.FROM_START, range: RANGES.COMMON };
}

/**
 * Plage effective d'une sélection sur les sessions chargées. Bornes « custom »
 * ramenées aux données ; plage commune indisponible en heure réelle (aucun
 * chevauchement) : repli sur « Tout », sans changer de mode.
 * @returns {{ align, range, commonUnavailable: boolean,
 *   clock: {start, end}|null, elapsed: number|null,
 *   windows: Array<{start, end}|null> }}
 *   `clock` : fenêtre commune (heure réelle) ; `elapsed` : durée depuis le
 *   début (« Depuis le début ») ; `windows` : fenêtre de chaque session en ms.
 */
export function resolveRange(loaded, selection) {
  const align = ALIGNS.includes(selection?.align) ? selection.align : defaultSelection(loaded).align;
  let range = selection?.range || RANGES.COMMON;
  if (align === ALIGN_MODES.FROM_START) {
    if (range === RANGES.CUSTOM) range = RANGES.COMMON;
    const elapsed = range === RANGES.ALL ? fullElapsed(loaded) : commonElapsed(loaded);
    const windows = loaded.map(s => {
      const origin = sessionOrigin(s);
      return Number.isFinite(origin) && isFiniteNumber(elapsed) ? { start: origin, end: origin + elapsed } : null;
    });
    return { align, range, commonUnavailable: false, clock: null, elapsed, windows };
  }
  const full = clockFullRange(loaded);
  const common = clockCommonRange(loaded);
  let clock = full;
  const commonUnavailable = !common;
  if (range === RANGES.COMMON && common) clock = common;
  if (range === RANGES.CUSTOM && full) {
    const start = Math.max(full.start, selection.from);
    const end = Math.min(full.end, selection.to);
    if (start < end) clock = { start, end };
    else range = RANGES.ALL;
  }
  if (range === RANGES.COMMON && !common) range = RANGES.ALL;
  return { align, range, commonUnavailable, clock, elapsed: null, windows: loaded.map(() => clock) };
}

/** Relevés dont l'horodatage tombe dans la fenêtre (bornes incluses). */
export function filterReadings(readings, window) {
  if (!window) return readings || [];
  return (readings || []).filter(r => {
    const t = toMs(r.timestamp);
    return t >= window.start && t <= window.end;
  });
}

/** Sessions limitées à leur fenêtre : mêmes objets `{ session, … }`, relevés filtrés. */
export function filterLoaded(loaded, resolved) {
  return loaded.map((s, i) => ({ ...s, readings: filterReadings(s.readings, resolved.windows[i]) }));
}

function medianInterval(readings) {
  const gaps = [];
  for (let i = 1; i < readings.length; i += 1) gaps.push(toMs(readings[i].timestamp) - toMs(readings[i - 1].timestamp));
  if (!gaps.length) return 0;
  gaps.sort((a, b) => a - b);
  return gaps[Math.floor(gaps.length / 2)];
}

/**
 * Part de la fenêtre couverte par les mesures d'une session (0 à 1) : somme de
 * min(intervalle, plafond) sur les mesures consécutives de la fenêtre, divisée
 * par la durée de la fenêtre. Une ligne présente compte même si ses valeurs
 * sont nulles (trou de mesure visible sur la courbe, D190) ; un début tardif,
 * une fin précoce ou un trou sans ligne comptent comme non couverts.
 * `medianMs` : intervalle médian de référence quand `allReadings` n'est qu'une
 * partie de la session (lien de séance sur une période, D247).
 */
export function coverageRatio(allReadings, window, medianMs) {
  if (!window || window.end <= window.start) return 1;
  const inside = filterReadings(allReadings, window);
  const median = Number.isFinite(medianMs) ? medianMs : medianInterval(allReadings || []);
  const cap = Math.max(MIN_GAP_CAP_MS, GAP_FACTOR * median);
  let covered = 0;
  for (let i = 1; i < inside.length; i += 1) {
    covered += Math.min(toMs(inside[i].timestamp) - toMs(inside[i - 1].timestamp), cap);
  }
  return Math.min(1, covered / (window.end - window.start));
}

/** Nombre de valeurs finies d'un champ. */
export const countMeasured = (readings, field) => (readings || []).filter(r => isFiniteNumber(r[field])).length;

/** Durée retenue d'une session dans sa fenêtre (intersection avec ses mesures), en ms. */
export function retainedDuration(s, window) {
  const span = sessionSpan(s);
  if (!span) return 0;
  if (!window) return span.end - span.start;
  return Math.max(0, Math.min(span.end, window.end) - Math.max(span.start, window.start));
}

/**
 * Champs dont la coche « meilleure valeur » est masquée : une session au moins
 * a moins de MIN_MEASURES_FOR_BEST valeurs de ce champ dans la plage (une
 * session qui ne le mesure pas du tout est ignorée : « Non mesuré » suffit).
 */
export function fieldsBelowMinimum(filtered, fields) {
  return fields.filter(field => filtered.some(s => {
    const n = countMeasured(s.readings, field);
    return n > 0 && n < MIN_MEASURES_FOR_BEST;
  }));
}

/** Lignes du tableau sans coche pour les champs donnés (compareTableRows inchangé). */
export function withoutBestMarks(rows, fields) {
  if (!fields.length) return rows;
  return rows.map(row => (fields.includes(row.metric.field)
    ? { ...row, cells: row.cells.map(cell => ({ ...cell, isBest: false })) }
    : row));
}

const URL_KEYS = Object.freeze(['align', 'range', 'from', 'to']);

/**
 * Sélection lue dans l'URL, ou null si aucun paramètre lisible. Paramètre
 * illisible ignoré ; from/to valides impliquent l'heure réelle.
 * @param {(key: string) => string|null} get : URLSearchParams.get
 */
export function parseRangeParams(get) {
  const from = Number(get('from'));
  const to = Number(get('to'));
  if (get('from') && get('to') && Number.isFinite(from) && Number.isFinite(to) && from < to) {
    return { align: ALIGN_MODES.CLOCK, range: RANGES.CUSTOM, from, to };
  }
  const align = ALIGNS.includes(get('align')) ? get('align') : null;
  const range = [RANGES.COMMON, RANGES.ALL].includes(get('range')) ? get('range') : null;
  if (!align && !range) return null;
  return { ...(align ? { align } : {}), range: range || RANGES.COMMON };
}

/** Paramètres d'URL d'une sélection (clés absentes = à retirer). */
export function rangeToParams(selection) {
  if (selection.range === RANGES.CUSTOM) {
    return { align: ALIGN_MODES.CLOCK, from: String(Math.round(selection.from)), to: String(Math.round(selection.to)) };
  }
  return { align: selection.align, range: selection.range };
}

/** Copie de `params` (URLSearchParams) avec la sélection écrite, autres clés conservées. */
export function writeRangeParams(params, selection) {
  const next = new URLSearchParams(params);
  URL_KEYS.forEach(key => next.delete(key));
  Object.entries(rangeToParams(selection)).forEach(([key, value]) => next.set(key, value));
  return next;
}
